import {
  Activity,
  BarChart3,
  Brain,
  Cpu,
  Ear,
  Layers,
  Search,
  Settings,
  Share2,
  TrendingUp,
  Users,
  Zap,
  UserCheck,
  Headset,
} from 'lucide-react-native'

export const NAV_LINKS = [
  { name: 'Features', path: '/features' as const },
  { name: 'Pricing', path: '/pricing' as const },
  { name: 'Vision', path: '/vision' as const },
]

export const PROMPTS = [
  'Only let urgent work calls through after 6pm.',
  'Move recruiters to email.',
  'Let family bypass AI screening.',
  'Send unknown callers to screening.',
  'Redirect sales enquiries to WhatsApp.',
  'Silently reject spam calls.',
]

export const FEATURE_ITEMS = [
  {
    icon: Ear,
    title: 'Easy Setup',
    description:
      "Tell Relayably what you want in plain English, like 'send recruiters to email' or 'always let family through'.",
  },
  {
    icon: Activity,
    title: 'Answers and Screens Calls',
    description:
      'Unknown callers are answered straight away, important people get through, and spam is kept out of your day.',
  },
  {
    icon: Brain,
    title: 'Remembers What Matters',
    description:
      'It keeps track of who called, what they needed, and what happened next, so you are not piecing things together later.',
  },
  {
    icon: UserCheck,
    title: 'Sounds Like Your Business',
    description:
      "Choose a voice that fits your business so callers get a helpful, professional experience even when you can't answer.",
  },
  {
    icon: Search,
    title: 'Handles the Follow-Up',
    description:
      'Book appointments, send summaries, and pass on the right details without extra admin at the end of every call.',
  },
  {
    icon: Share2,
    title: 'Built to Grow With You',
    description:
      'Start with calls today, with more ways to stay responsive as your business grows.',
  },
]

export const SALES_STACK = [
  'Lead capture',
  'Call routing',
  'Appointment Booking',
  'Sales follow-up',
  'CRM updates',
  'Call insights',
  'Professional call answering',
  'Lead sorting',
  'Business voice options',
]

export const OPS_STACK = [
  'Call screening',
  'Priority contacts',
  'Urgent call handoff',
  'Smarter voicemail handling',
  'Custom call handling',
  'Call history',
  'Better caller context',
  'Support for more channels',
  'Admin controls',
]

export const PILLARS = [
  {
    icon: Layers,
    title: '1. Keep Setup Simple',
    description:
      'Make getting started feel easy, with clear steps, plain language, and sensible defaults.',
  },
  {
    icon: Activity,
    title: '2. Help People Stay Responsive',
    description:
      'Make sure calls are answered, the right people get through, and important moments are not missed.',
  },
  {
    icon: Zap,
    title: '3. Reduce Daily Interruptions',
    description:
      'Take pressure off busy people by handling the noise before it breaks their focus.',
  },
  {
    icon: Settings,
    title: '4. Keep People in Control',
    description:
      'Let every business decide how calls are handled without needing outside help to set it up.',
  },
  {
    icon: Cpu,
    title: '5. Grow Without Adding Friction',
    description:
      'Use AI to save time, stay professional, and handle more conversations without making the experience harder.',
  },
]

export const privacySections = [
  {
    title: '1. Information We Collect',
    body: [
      'When you submit forms through the Platform, we may collect: full name, email, phone, country, usage type, and product feedback. We may also automatically collect browser and device information, IP addresses, and analytics data.',
    ],
  },
  {
    title: '2. How We Use Information',
    listLabel: 'We use collected information to:',
    list: [
      'Understand market interest and evaluate product demand;',
      'Contact potential users and prioritise beta access;',
      'Improve product direction and website performance;',
      'Communicate updates and announcements.',
    ],
  },
  {
    title: '3. Firebase and Third-Party Services',
    body: [
      'Information submitted through forms may be stored using Firebase and Google Cloud Platform. We may also use third-party analytics and hosting providers. These providers may process data in countries outside your own.',
    ],
  },
  {
    title: '4. Marketing Communications',
    body: [
      'By submitting your information, you may receive product updates, early-access invitations, and marketing communications. You may unsubscribe at any time using the link provided in communications.',
    ],
  },
  {
    title: '5. Data Retention',
    body: ['We retain information only as long as reasonably necessary for product development, market validation, and operational purposes. We may delete or anonymise data periodically.'],
  },
  {
    title: '6. Sharing Information',
    body: ['We do not sell your personal information. We may share information with service providers, infrastructure providers, and legal authorities where required by law.'],
  },
  {
    title: '7. Security',
    body: ['We take reasonable steps to protect information using secure cloud infrastructure and authentication controls. However, no internet-based service can be guaranteed completely secure.'],
  },
  {
    title: '8. International Users',
    body: ['By using the Platform, you understand that your information may be processed in countries outside your own jurisdiction.'],
  },
  {
    title: '9. Your Rights',
    body: ['Depending on your location, you may have rights to access, correct, or request deletion of your information.'],
  },
  {
    title: '10. Cookies and Analytics',
    body: ['The Platform may use cookies and analytics tools to understand website performance and user engagement. You can control cookies through your browser settings.'],
  },
  {
    title: '11. Future AI and Voice Features',
    body: ['If future versions include voice cloning or telephony services, additional consent and policies will apply before those features are activated.'],
  },
  {
    title: '12. Changes to This Policy',
    body: ['We may update this Privacy Policy periodically. Updated versions will be posted on this page with a revised "Last Updated" date.'],
  },
]

export const termsSections = [
  {
    title: '1. Purpose of the Platform',
    body: [
      'This website is an early-access and market validation platform designed to gather interest in a proposed AI-powered communications and virtual assistant service.',
      'The Platform does not currently provide live telephony, AI call handling, voice cloning, or production virtual assistant services. Submitting information through the Platform does not create a customer relationship, subscription, or guarantee future access to the product.',
    ],
    listLabel: 'The Platform currently:',
    list: ['Provides information about the proposed product;', 'Allows users to register interest;', 'Collects feedback and early-access applications.'],
  },
  {
    title: '2. Eligibility',
    body: ['You must be at least 18 years old to use this Platform. By using the Platform, you confirm that:'],
    list: ['You are legally capable of entering into agreements;', 'The information you provide is accurate;', 'You are authorised to submit any information you provide.'],
  },
  {
    title: '3. Early Access and Waitlist',
    body: [
      'Submitting an early-access application:',
      'We may contact users who submit forms for research, interviews, beta testing, product updates, marketing communications, or onboarding opportunities.',
    ],
    list: [
      'Does not guarantee acceptance into any beta or production program;',
      'Does not reserve pricing;',
      'Does not create any obligation for us to provide services;',
      'May be used to prioritise future access.',
    ],
  },
  {
    title: '4. No Production Services',
    body: ['The Platform is currently a pre-release concept and lead-generation experience. Features shown on the website, including AI call handling, voice cloning, call routing, and automations may not yet exist, may change substantially, or may never be released. All screenshots, interfaces, workflows, pricing, and feature descriptions are illustrative only.'],
  },
  {
    title: '5. Acceptable Use',
    body: ['You agree not to: use the Platform unlawfully; attempt to interfere with the Platform; scrape, reverse engineer, or copy the Platform; submit misleading or fraudulent information; upload malicious code or content; or impersonate another person.'],
  },
  {
    title: '6. Intellectual Property',
    body: ['All content on the Platform, including branding, designs, interfaces, and software, are owned by or licensed to us and protected by intellectual property laws. You may not reproduce or redistribute any content without written permission.'],
  },
  {
    title: '7. Voice and AI Features',
    body: ['Any references to AI-generated voices or voice cloning are conceptual and informational only at this stage. Future voice-related functionality may be subject to additional consent, identity verification, and local telecommunications laws.'],
  },
  {
    title: '8. Third-Party Services',
    body: ['The Platform may use third-party services including analytics, cloud infrastructure, and Firebase. We are not responsible for outages, failures, or actions of third-party services.'],
  },
  {
    title: '9. Limitation of Liability',
    body: ['To the maximum extent permitted by law, the Platform is provided "as is". We are not liable for loss of profits, business, data, or reliance on information presented on the Platform.'],
  },
  {
    title: '10. Privacy',
    body: ['Your use of the Platform is also governed by our Privacy Policy.'],
  },
  {
    title: '11. Changes to These Terms',
    body: ['We may update these Terms at any time without prior notice. Continued use of the Platform after changes are posted constitutes acceptance of the updated Terms.'],
  },
]
